import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
    DndContext,
    DragOverlay,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useDroppable,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Spinner } from 'react-bootstrap';
import {
    FaBook,
    FaChevronDown,
    FaChevronRight,
    FaCog,
    FaFolderOpen,
    FaKey,
    FaLayerGroup,
    FaLock,
    FaPlus,
    FaSearch,
    FaShieldAlt,
    FaSync,
    FaTimes,
    FaTrash,
    FaUsers,
} from 'react-icons/fa';
import { AuthContext } from '../context/AuthContext';
import api from '../services/api';
import { extractErrorMessage } from '../utils/errorUtils';
import { isSuperAdminUser } from '../utils/roleUtils';
import { CAPABILITIES, bucketForAction } from '../utils/featureAccess';
import './AccessManagement.css';

// ── Constants ─────────────────────────────────────────────────
const SYSTEM_ROLE_KEYS = new Set(['superadmin', 'admin', 'employee']);
const ACTION_SORT_ORDER = ['READ', 'CREATE', 'UPDATE', 'DELETE', '*'];
const UNASSIGNED_KEY = '__UNASSIGNED__';

const TABS = [
    {
        id: 'overview',
        label: 'Overview',
        subtitle: 'Summary & quick-start guide',
        icon: FaBook,
    },
    {
        id: 'roles',
        label: 'Role Permissions',
        subtitle: 'Grant API access to roles',
        icon: FaKey,
    },
    {
        id: 'employees',
        label: 'Employee Roles',
        subtitle: 'Assign roles to people',
        icon: FaUsers,
    },
    {
        id: 'features',
        label: 'Feature Groups',
        subtitle: 'Organise APIs into groups',
        icon: FaFolderOpen,
    },
    {
        id: 'register-api',
        label: 'Register API',
        subtitle: 'Add permission + UI in one step',
        icon: FaPlus,
    },
];

// ── Pure helpers ──────────────────────────────────────────────
const normalizeId = (value) => String(value ?? '').trim();
const normalizeRoleName = (name = '') => String(name).toLowerCase().replace(/[\s_-]/g, '');
const isSystemRole = (role) => SYSTEM_ROLE_KEYS.has(normalizeRoleName(role?.name));

const getEmployeeName = (employee) => {
    if (employee?.name?.trim()) return employee.name.trim();
    const fallback = [employee?.firstName, employee?.lastName].filter(Boolean).join(' ').trim();
    if (fallback) return fallback;
    return employee?.email || `Employee ${employee?.id || ''}`.trim();
};

const getPermissionName = (permission) => permission?.name?.trim() || 'Unknown Permission';

const actionRank = (action) => {
    const idx = ACTION_SORT_ORDER.indexOf(String(action || '').toUpperCase());
    return idx === -1 ? ACTION_SORT_ORDER.length : idx;
};

const sortPermissions = (a, b) => {
    const diff = actionRank(a?.action) - actionRank(b?.action);
    if (diff !== 0) return diff;
    return getPermissionName(a).localeCompare(getPermissionName(b));
};

const groupPermissionsByFeature = (permissions = [], featureGroups = []) => {
    const grouped = new Map();

    featureGroups.forEach((group) => {
        const id = normalizeId(group.id);
        if (!id) return;
        grouped.set(id, {
            featureKey: id,
            feature: {
                id,
                name: group.name || 'Unnamed Group',
                label: group.name || 'Unnamed Group',
                description: group.description || '',
                icon: FaLayerGroup,
            },
            permissions: [],
        });
    });

    grouped.set(UNASSIGNED_KEY, {
        featureKey: UNASSIGNED_KEY,
        feature: {
            id: UNASSIGNED_KEY,
            name: 'Unassigned APIs',
            label: 'Unassigned APIs',
            description: 'APIs not yet assigned to any Feature Group',
            icon: FaLayerGroup,
        },
        permissions: [],
    });

    permissions.forEach((permission) => {
        const featureGroupId = normalizeId(permission?.featureGroupId);
        const key = featureGroupId || UNASSIGNED_KEY;

        if (!grouped.has(key)) {
            grouped.set(key, {
                featureKey: key,
                feature: {
                    id: key,
                    name: 'Unknown Group',
                    label: 'Unknown Group',
                    description: 'Linked group no longer exists.',
                    icon: FaLayerGroup,
                },
                permissions: [],
            });
        }

        grouped.get(key).permissions.push(permission);
    });

    const result = Array.from(grouped.values()).map((bucket) => ({
        ...bucket,
        permissions: [...bucket.permissions].sort(sortPermissions),
    }));

    result.sort((a, b) => {
        if (a.featureKey === UNASSIGNED_KEY) return 1;
        if (b.featureKey === UNASSIGNED_KEY) return -1;
        return (a.feature.label || '').localeCompare(b.feature.label || '');
    });

    return result;
};

const permissionMatchesSearch = (permission, searchTerm) => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return true;
    return [getPermissionName(permission), permission?.action]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term));
};

const toggleInSet = (set, value, enabled) => {
    const next = new Set(set);
    if (enabled) next.add(value);
    else next.delete(value);
    return next;
};

// ── Shared micro-components ───────────────────────────────────

const ToggleSwitch = ({ checked, disabled, onClick, ariaLabel, small = false }) => (
    <button
        type="button"
        className={`am-toggle ${checked ? 'on' : ''} ${small ? 'sm' : ''}`}
        onClick={onClick}
        disabled={disabled}
        aria-label={ariaLabel}
    >
        <span className="am-toggle-thumb" />
    </button>
);

const ActionPill = ({ action }) => {
    const normalized = String(action || '*').toUpperCase();
    const styles = {
        READ: { background: 'rgba(108, 182, 255, 0.22)', color: '#cde6ff' },
        CREATE: { background: 'rgba(99, 220, 174, 0.22)', color: '#c9f9e4' },
        UPDATE: { background: 'rgba(255, 198, 94, 0.21)', color: '#ffebc4' },
        DELETE: { background: 'rgba(244, 122, 145, 0.22)', color: '#ffd1da' },
        '*': { background: 'rgba(146, 158, 185, 0.2)', color: '#d8e0f2' },
    };
    const style = styles[normalized] || styles['*'];
    return (
        <span className="am-action-pill" style={style}>
            {normalized}
        </span>
    );
};

const InlineAlert = ({ type, children, onDismiss }) => (
    <div className={`am-inline-alert ${type}`}>
        {type === 'error' ? <FaTimes /> : <FaLock />}
        <span>{children}</span>
        {onDismiss && (
            <button type="button" onClick={onDismiss} aria-label="Dismiss">
                <FaTimes size={12} />
            </button>
        )}
    </div>
);

// ── Overview Tab ──────────────────────────────────────────────

const OverviewTab = ({ roles, employees, permissions, groupedFeatures, onNavigate }) => (
    <div className="am-overview-screen">
        {/* Quick stats */}
        <div className="am-quickstats-grid">
            <div className="am-quickstat-card">
                <span className="am-quickstat-label">Total Roles</span>
                <span className="am-quickstat-value">{roles.length}</span>
                <span className="am-quickstat-sub">
                    {roles.filter(isSystemRole).length} system · {roles.filter((r) => !isSystemRole(r)).length} custom
                </span>
            </div>
            <div className="am-quickstat-card">
                <span className="am-quickstat-label">Employees</span>
                <span className="am-quickstat-value">{employees.length}</span>
                <span className="am-quickstat-sub">People with access</span>
            </div>
            <div className="am-quickstat-card">
                <span className="am-quickstat-label">API Permissions</span>
                <span className="am-quickstat-value">{permissions.length}</span>
                <span className="am-quickstat-sub">Across all roles</span>
            </div>
            <div className="am-quickstat-card">
                <span className="am-quickstat-label">Feature Groups</span>
                <span className="am-quickstat-value">{groupedFeatures.filter((g) => g.featureKey !== UNASSIGNED_KEY).length}</span>
                <span className="am-quickstat-sub">Custom groups created</span>
            </div>
        </div>

        {/* Setup guide + Feature snapshot */}
        <div className="am-overview-row">
            <div className="am-overview-card am-panel">
                <header className="am-panel-head">
                    <p className="am-overview-card-title">Setup Guide</p>
                    <p className="am-overview-card-desc">Follow these steps in order to configure access control.</p>
                </header>
                <div className="am-steps-list am-panel-body">
                    <button type="button" className="am-step-item" onClick={() => onNavigate('features')}>
                        <span className="am-step-number">1</span>
                        <div className="am-step-content">
                            <strong>Create Feature Groups</strong>
                            <small>Organise your APIs into logical business areas.</small>
                        </div>
                        <FaChevronRight className="am-step-arrow" />
                    </button>
                    <button type="button" className="am-step-item" onClick={() => onNavigate('roles')}>
                        <span className="am-step-number">2</span>
                        <div className="am-step-content">
                            <strong>Assign Permissions to Roles</strong>
                            <small>Choose which API actions each role can perform.</small>
                        </div>
                        <FaChevronRight className="am-step-arrow" />
                    </button>
                    <button type="button" className="am-step-item" onClick={() => onNavigate('employees')}>
                        <span className="am-step-number">3</span>
                        <div className="am-step-content">
                            <strong>Assign Roles to Employees</strong>
                            <small>Map your team members to the right roles.</small>
                        </div>
                        <FaChevronRight className="am-step-arrow" />
                    </button>
                </div>
            </div>

            <div className="am-overview-card am-panel">
                <header className="am-panel-head">
                    <p className="am-overview-card-title">API Distribution by Feature Group</p>
                    <p className="am-overview-card-desc">How your API permissions are currently grouped.</p>
                </header>
                <div className="am-feature-snapshot-grid am-panel-body">
                    {groupedFeatures.map((group) => {
                        const Icon = group.feature.icon || FaLayerGroup;
                        return (
                            <div key={group.featureKey} className="am-snapshot-card">
                                <span className="am-snapshot-icon">
                                    <Icon />
                                </span>
                                <div className="am-snapshot-info">
                                    <h4>{group.feature.label}</h4>
                                    <p>{group.feature.description || 'No description'}</p>
                                </div>
                                <span className="am-snapshot-count">{group.permissions.length} APIs</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    </div>
);

// ── Role Permissions Tab ──────────────────────────────────────

const RolesTab = ({ roles, permissions, featureGroups, canManage, onRolesChange }) => {
    const [selectedRoleId, setSelectedRoleId] = useState(null);
    const [search, setSearch] = useState('');
    const [newRoleName, setNewRoleName] = useState('');
    const [creatingRole, setCreatingRole] = useState(false);
    const [deletingRoleId, setDeletingRoleId] = useState(null);
    const [pendingPermissionKeys, setPendingPermissionKeys] = useState(new Set());
    const [collapsedFeatures, setCollapsedFeatures] = useState({});
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!roles.length) { setSelectedRoleId(null); return; }
        const hasSelected = roles.some((r) => normalizeId(r.id) === normalizeId(selectedRoleId));
        if (!hasSelected) setSelectedRoleId(roles[0].id);
    }, [roles, selectedRoleId]);

    const selectedRole = useMemo(
        () => roles.find((r) => normalizeId(r.id) === normalizeId(selectedRoleId)) || null,
        [roles, selectedRoleId]
    );

    const assignedPermissionIds = useMemo(
        () => new Set((selectedRole?.permissions || []).map((p) => normalizeId(p.id))),
        [selectedRole]
    );

    const filteredPermissions = useMemo(
        () => permissions.filter((p) => permissionMatchesSearch(p, search)),
        [permissions, search]
    );

    const groupedFeatures = useMemo(
        () => groupPermissionsByFeature(filteredPermissions, featureGroups),
        [filteredPermissions, featureGroups]
    );

    const refreshRoles = useCallback(async () => {
        const res = await api.get('/access/roles', { params: { _t: Date.now() } });
        const latest = Array.isArray(res.data) ? res.data : [];
        onRolesChange(latest);
        return latest;
    }, [onRolesChange]);

    const updateRolePermissions = useCallback(
        async (roleId, permissionIds, touchedIds = []) => {
            const keys = touchedIds.map((pid) => `${normalizeId(roleId)}:${normalizeId(pid)}`);
            setPendingPermissionKeys((prev) => { const n = new Set(prev); keys.forEach((k) => n.add(k)); return n; });
            try {
                await api.post(`/access/roles/${roleId}/permissions`, { permissionIds });
                await refreshRoles();
            } finally {
                setPendingPermissionKeys((prev) => { const n = new Set(prev); keys.forEach((k) => n.delete(k)); return n; });
            }
        },
        [refreshRoles]
    );

    const handleTogglePermission = async (permission) => {
        if (!selectedRole || !canManage || isSystemRole(selectedRole)) return;
        const pid = normalizeId(permission.id);
        const current = new Set((selectedRole.permissions || []).map((p) => normalizeId(p.id)));
        const next = toggleInSet(current, pid, !current.has(pid));
        try { await updateRolePermissions(selectedRole.id, Array.from(next), [pid]); }
        catch (err) { setError(await extractErrorMessage(err, 'Failed to update permission.')); }
    };

    // Grant/revoke a whole capability bucket (read | write | delete) within a
    // feature group in one call. `ids` are the permission ids whose action falls
    // in that bucket (computed per feature via bucketForAction).
    const handleToggleCapability = async (ids, allAssigned) => {
        if (!selectedRole || !canManage || isSystemRole(selectedRole) || !ids.length) return;
        const current = new Set((selectedRole.permissions || []).map((p) => normalizeId(p.id)));
        if (allAssigned) ids.forEach((id) => current.delete(id));
        else ids.forEach((id) => current.add(id));
        try { await updateRolePermissions(selectedRole.id, Array.from(current), ids); }
        catch (err) { setError(await extractErrorMessage(err, 'Failed to update feature permissions.')); }
    };

    const handleCreateRole = async () => {
        if (!canManage || !newRoleName.trim()) return;
        setCreatingRole(true); setError(null);
        try {
            const name = newRoleName.trim();
            await api.post('/access/roles', { name });
            const latest = await refreshRoles();
            const created = latest.find((r) => normalizeRoleName(r.name) === normalizeRoleName(name));
            setSelectedRoleId(created?.id || latest[0]?.id || null);
            setNewRoleName('');
        } catch (err) { setError(await extractErrorMessage(err, 'Failed to create role.')); }
        finally { setCreatingRole(false); }
    };

    const handleDeleteRole = async (role) => {
        if (!role || !canManage || isSystemRole(role)) return;
        if (!window.confirm(`Delete role "${role.name}"? This cannot be undone.`)) return;
        setDeletingRoleId(role.id); setError(null);
        try {
            await api.delete(`/access/roles/${role.id}`);
            const latest = await refreshRoles();
            if (normalizeId(role.id) === normalizeId(selectedRoleId))
                setSelectedRoleId(latest[0]?.id || null);
        } catch (err) { setError(await extractErrorMessage(err, 'Failed to delete role.')); }
        finally { setDeletingRoleId(null); }
    };

    return (
        <div className="am-split-layout">
            {/* Left sidebar — role list */}
            <aside className="am-sidebar">
                <div className="am-sidebar-header">
                    <h3>Roles</h3>
                    <span className="am-count-badge">{roles.length}</span>
                </div>

                <div className="am-sidebar-list">
                    {roles.map((role) => {
                        const selected = normalizeId(role.id) === normalizeId(selectedRoleId);
                        const deleting = normalizeId(deletingRoleId) === normalizeId(role.id);

                        return (
                            <button
                                key={role.id}
                                type="button"
                                className={`am-list-item am-role-list-item ${selected ? 'active' : ''}`}
                                onClick={() => setSelectedRoleId(role.id)}
                            >
                                <div>
                                    <div className="am-list-item-title">{role.name}</div>
                                    <div className="am-list-item-meta">{(role.permissions || []).length} permissions</div>
                                </div>
                                <div className="am-list-item-end">
                                    <span className={`am-role-type ${isSystemRole(role) ? 'system' : 'custom'}`}>
                                        {isSystemRole(role) ? 'System' : 'Custom'}
                                    </span>
                                    {canManage && !isSystemRole(role) && (
                                        <button
                                            type="button"
                                            className="am-icon-btn danger"
                                            onClick={(e) => { e.stopPropagation(); handleDeleteRole(role); }}
                                            disabled={deleting}
                                            aria-label={`Delete role ${role.name}`}
                                        >
                                            {deleting ? <Spinner animation="border" size="sm" /> : <FaTrash size={11} />}
                                        </button>
                                    )}
                                </div>
                            </button>
                        );
                    })}
                </div>

                {canManage && (
                    <div className="am-sidebar-footer">
                        <input
                            className="am-input"
                            placeholder="New role name…"
                            value={newRoleName}
                            onChange={(e) => setNewRoleName(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleCreateRole()}
                        />
                        <button
                            type="button"
                            className="am-primary-btn"
                            onClick={handleCreateRole}
                            disabled={!newRoleName.trim() || creatingRole}
                        >
                            {creatingRole ? <Spinner animation="border" size="sm" /> : <><FaPlus size={10} /> Add</>}
                        </button>
                    </div>
                )}
            </aside>

            {/* Right panel — permission editor */}
            <section className="am-detail-panel">
                {!selectedRole && (
                    <div className="am-empty-state">
                        <FaShieldAlt />
                        <p>Select a role from the list to manage its API permissions.</p>
                    </div>
                )}

                {selectedRole && (
                    <>
                        <div className="am-detail-panel-header">
                            <div>
                                <h2>{selectedRole.name}</h2>
                                <p>
                                    {(selectedRole.permissions || []).length} of {permissions.length} APIs assigned
                                    {isSystemRole(selectedRole) ? ' · System role is read-only' : ''}
                                </p>
                            </div>

                            <div className="am-search-wrap">
                                <FaSearch className="am-search-icon" size={13} />
                                <input
                                    className="am-search-input"
                                    placeholder="Search APIs…"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="am-detail-panel-body">
                            {error && (
                                <InlineAlert type="error" onDismiss={() => setError(null)}>{error}</InlineAlert>
                            )}

                            {groupedFeatures.length === 0 && (
                                <div className="am-empty-state compact">
                                    <p>No APIs matched your search.</p>
                                </div>
                            )}

                            <div className="am-feature-section-list">
                                {groupedFeatures.map((fg) => {
                                    const Icon = fg.feature.icon || FaLayerGroup;
                                    const assignedCount = fg.permissions.filter((p) =>
                                        assignedPermissionIds.has(normalizeId(p.id))
                                    ).length;
                                    const expanded = collapsedFeatures[fg.featureKey] !== true;
                                    // Bucket this feature's APIs into read / write / delete so the
                                    // Super Admin grants by capability, not one endpoint at a time.
                                    // Actions with no bucket (execute/manage) stay on the rows below.
                                    const capabilityButtons = CAPABILITIES.map((cap) => {
                                        const perms = fg.permissions.filter((p) => bucketForAction(p.action) === cap);
                                        const ids = perms.map((p) => normalizeId(p.id));
                                        const assigned = ids.filter((id) => assignedPermissionIds.has(id)).length;
                                        const pending = ids.some((id) =>
                                            pendingPermissionKeys.has(`${normalizeId(selectedRole.id)}:${id}`));
                                        return {
                                            cap, ids, pending,
                                            count: perms.length,
                                            allAssigned: perms.length > 0 && assigned === perms.length,
                                        };
                                    }).filter((b) => b.count > 0);

                                    return (
                                        <article key={fg.featureKey} className="am-feature-section">
                                            <header className="am-feature-section-header">
                                                <div className="am-feature-section-title">
                                                    <span className="am-feature-section-icon"><Icon /></span>
                                                    <div>
                                                        <h4>{fg.feature.label}</h4>
                                                        {fg.feature.description && (
                                                            <p>{fg.feature.description}</p>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="am-feature-section-controls">
                                                    <span className="am-progress-badge">
                                                        {assignedCount}/{fg.permissions.length}
                                                    </span>
                                                    {canManage && capabilityButtons.length > 0 && (
                                                        <div className="am-cap-toggles">
                                                            {capabilityButtons.map((b) => (
                                                                <div key={b.cap} className="am-cap-toggle-item">
                                                                    <span className="am-toggle-label">{b.cap}</span>
                                                                    {b.pending ? (
                                                                        <Spinner animation="border" size="sm" className="am-spinner" />
                                                                    ) : (
                                                                        <ToggleSwitch
                                                                            small
                                                                            checked={b.allAssigned}
                                                                            disabled={!canManage || isSystemRole(selectedRole)}
                                                                            onClick={() => handleToggleCapability(b.ids, b.allAssigned)}
                                                                            ariaLabel={`${b.allAssigned ? 'Revoke' : 'Grant'} ${b.cap} in ${fg.feature.label}`}
                                                                        />
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                    <button
                                                        type="button"
                                                        className="am-icon-btn"
                                                        onClick={() =>
                                                            setCollapsedFeatures((prev) => ({
                                                                ...prev,
                                                                [fg.featureKey]: expanded,
                                                            }))
                                                        }
                                                        aria-label={`${expanded ? 'Collapse' : 'Expand'} ${fg.feature.label}`}
                                                    >
                                                        {expanded ? <FaChevronDown size={11} /> : <FaChevronRight size={11} />}
                                                    </button>
                                                </div>
                                            </header>

                                            {expanded && (
                                                <div className="am-feature-section-body">
                                                    {fg.permissions.map((permission) => {
                                                        const assigned = assignedPermissionIds.has(normalizeId(permission.id));
                                                        const pk = `${normalizeId(selectedRole.id)}:${normalizeId(permission.id)}`;
                                                        const pending = pendingPermissionKeys.has(pk);

                                                        return (
                                                            <div key={permission.id} className={`am-api-row ${assigned ? 'assigned' : ''}`}>
                                                                <div className="am-api-title">{getPermissionName(permission)}</div>
                                                                <div className="am-api-controls">
                                                                    <ActionPill action={permission.action} />
                                                                    {pending ? (
                                                                        <Spinner animation="border" size="sm" className="am-spinner" />
                                                                    ) : (
                                                                        <ToggleSwitch
                                                                            checked={assigned}
                                                                            small
                                                                            disabled={!canManage || isSystemRole(selectedRole)}
                                                                            onClick={() => handleTogglePermission(permission)}
                                                                            ariaLabel={`${assigned ? 'Revoke' : 'Grant'} ${getPermissionName(permission)}`}
                                                                        />
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </article>
                                    );
                                })}
                            </div>
                        </div>
                    </>
                )}
            </section>
        </div>
    );
};

// ── Employee Roles Tab ────────────────────────────────────────

const EmployeesTab = ({ roles, employees, canManage, onEmployeesChange }) => {
    const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
    const [search, setSearch] = useState('');
    const [pendingRoleKeys, setPendingRoleKeys] = useState(new Set());
    const [error, setError] = useState(null);
    const [syncing, setSyncing] = useState(false);
    const [syncResult, setSyncResult] = useState(null);

    useEffect(() => {
        if (!employees.length) { setSelectedEmployeeId(null); return; }
        const hasSelected = employees.some((e) => normalizeId(e.id) === normalizeId(selectedEmployeeId));
        if (!hasSelected) setSelectedEmployeeId(employees[0].id);
    }, [employees, selectedEmployeeId]);

    const selectedEmployee = useMemo(
        () => employees.find((e) => normalizeId(e.id) === normalizeId(selectedEmployeeId)) || null,
        [employees, selectedEmployeeId]
    );

    const filteredEmployees = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return employees;
        return employees.filter((e) => {
            const roleNames = (e.roles || []).map((r) => r.name).join(' ');
            return [getEmployeeName(e), e?.email, e?.department, e?.title, roleNames]
                .filter(Boolean)
                .some((v) => String(v).toLowerCase().includes(term));
        });
    }, [employees, search]);

    const refreshEmployees = useCallback(async () => {
        const res = await api.get('/employees', { params: { _t: Date.now() } });
        const latest = Array.isArray(res.data) ? res.data : [];
        onEmployeesChange(latest);
        return latest;
    }, [onEmployeesChange]);

    // Pulls the Homebase roster (POST /api/employees/sync). Additive by design —
    // the backend refreshes name/email/department/PIN and only assigns a role to
    // someone who has none, so nobody's existing access changes. Super Admin only
    // (canManage), and confirmed because it rewrites every employee record.
    const handleSync = async () => {
        if (!canManage || syncing) return;
        const proceed = window.confirm(
            'Sync employees from Homebase?\n\n'
            + 'Name, email, department and PIN are refreshed for everyone on the roster, '
            + 'and anyone new is added. Employees who already have a role keep it; '
            + 'those without one are given Sales (managers get Admin).'
        );
        if (!proceed) return;

        setSyncing(true);
        setError(null);
        setSyncResult(null);
        try {
            const res = await api.post('/employees/sync');
            setSyncResult(typeof res.data === 'string' ? res.data : 'Employees synced.');
            await refreshEmployees();
        } catch (err) {
            setError(await extractErrorMessage(err, 'Failed to sync employees from Homebase.'));
        } finally {
            setSyncing(false);
        }
    };

    const handleToggleRole = async (roleId) => {
        if (!selectedEmployee || !canManage) return;
        const empId = normalizeId(selectedEmployee.id);
        const roleToken = normalizeId(roleId);
        const key = `${empId}:${roleToken}`;
        setPendingRoleKeys((prev) => new Set(prev).add(key));
        try {
            const current = (selectedEmployee.roles || []).map((r) => normalizeId(r.id));
            const next = current.includes(roleToken)
                ? current.filter((id) => id !== roleToken)
                : [...current, roleToken];
            await api.post(`/access/employees/${selectedEmployee.id}/roles`, { roleIds: next });
            await refreshEmployees();
        } catch (err) {
            setError(await extractErrorMessage(err, 'Failed to update employee roles.'));
        } finally {
            setPendingRoleKeys((prev) => { const n = new Set(prev); n.delete(key); return n; });
        }
    };

    return (
        <div className="am-split-layout">
            {/* Left sidebar */}
            <aside className="am-sidebar">
                <div className="am-sidebar-header">
                    <h3>Employees</h3>
                    <div className="am-sidebar-header-actions">
                        <span className="am-count-badge">{employees.length}</span>
                        {canManage && (
                            <button
                                type="button"
                                className="am-sync-btn"
                                onClick={handleSync}
                                disabled={syncing}
                                title="Pull the current roster from Homebase"
                            >
                                <FaSync className={syncing ? 'am-spin' : ''} size={11} />
                                {syncing ? 'Syncing…' : 'Sync'}
                            </button>
                        )}
                    </div>
                </div>

                {syncResult && (
                    <div className="am-sidebar-notice">
                        <InlineAlert type="info" onDismiss={() => setSyncResult(null)}>
                            {syncResult}
                        </InlineAlert>
                    </div>
                )}
                {error && !selectedEmployee && (
                    <div className="am-sidebar-notice">
                        <InlineAlert type="error" onDismiss={() => setError(null)}>{error}</InlineAlert>
                    </div>
                )}

                <div className="am-sidebar-search">
                    <FaSearch size={12} />
                    <input
                        className="am-search-input"
                        placeholder="Search by name, email, department…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>

                <div className="am-sidebar-list">
                    {filteredEmployees.map((emp) => {
                        const selected = normalizeId(emp.id) === normalizeId(selectedEmployeeId);
                        return (
                            <button
                                key={emp.id}
                                type="button"
                                className={`am-list-item am-employee-list-item ${selected ? 'active' : ''}`}
                                onClick={() => setSelectedEmployeeId(emp.id)}
                            >
                                <div className="am-avatar">{getEmployeeName(emp).charAt(0).toUpperCase()}</div>
                                <div>
                                    <div className="am-employee-name">{getEmployeeName(emp)}</div>
                                    <div className="am-employee-meta">{emp?.department || emp?.title || emp?.email}</div>
                                    <div className="am-chip-row">
                                        {(emp.roles || []).map((r) => (
                                            <span key={r.id} className="am-role-chip">{r.name}</span>
                                        ))}
                                    </div>
                                </div>
                            </button>
                        );
                    })}

                    {filteredEmployees.length === 0 && (
                        <div className="am-empty-state compact">
                            <p>No employees match your search.</p>
                        </div>
                    )}
                </div>
            </aside>

            {/* Right detail panel */}
            <section className="am-detail-panel">
                {!selectedEmployee && (
                    <div className="am-empty-state">
                        <FaUsers />
                        <p>Select an employee from the list to manage their role assignments.</p>
                    </div>
                )}

                {selectedEmployee && (
                    <>
                        <div className="am-detail-panel-header">
                            <div className="am-person-header">
                                <div className="am-avatar lg">{getEmployeeName(selectedEmployee).charAt(0).toUpperCase()}</div>
                                <div>
                                    <h2>{getEmployeeName(selectedEmployee)}</h2>
                                    <p>{selectedEmployee.email}</p>
                                </div>
                            </div>
                        </div>

                        <div className="am-detail-panel-body">
                            {error && (
                                <InlineAlert type="error" onDismiss={() => setError(null)}>{error}</InlineAlert>
                            )}
                            {!canManage && (
                                <InlineAlert type="info">
                                    Read-only · Only Super Admin can change role assignments.
                                </InlineAlert>
                            )}

                            <div className="am-role-assignment-list">
                                {roles.map((role) => {
                                    const assigned = (selectedEmployee.roles || []).some(
                                        (r) => normalizeId(r.id) === normalizeId(role.id)
                                    );
                                    const pending = pendingRoleKeys.has(
                                        `${normalizeId(selectedEmployee.id)}:${normalizeId(role.id)}`
                                    );

                                    return (
                                        <div key={role.id} className={`am-role-assignment-item ${assigned ? 'assigned' : ''}`}>
                                            <div>
                                                <div className="am-role-assignment-title">{role.name}</div>
                                                <div className="am-role-assignment-meta">
                                                    <span className={`am-role-type ${isSystemRole(role) ? 'system' : 'custom'}`}>
                                                        {isSystemRole(role) ? 'System Role' : 'Custom Role'}
                                                    </span>
                                                    <span>{(role.permissions || []).length} permissions</span>
                                                </div>
                                            </div>
                                            <div>
                                                {pending ? (
                                                    <Spinner animation="border" size="sm" className="am-spinner" />
                                                ) : (
                                                    <ToggleSwitch
                                                        checked={assigned}
                                                        small
                                                        disabled={!canManage}
                                                        onClick={() => handleToggleRole(role.id)}
                                                        ariaLabel={`${assigned ? 'Remove' : 'Assign'} ${role.name}`}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </>
                )}
            </section>
        </div>
    );
};

// ── Feature Groups Tab ────────────────────────────────────────

const FeatureGroupDropZone = ({ groupId, children }) => {
    const { setNodeRef, isOver } = useDroppable({
        id: `group:${groupId}`,
        data: { type: 'GROUP', groupId },
    });

    return (
        <div
            ref={setNodeRef}
            style={{
                minHeight: '44px',
                borderRadius: '8px',
                outline: isOver ? '1px dashed rgba(163, 161, 74, 0.7)' : 'none',
                outlineOffset: '2px',
            }}
        >
            {children}
        </div>
    );
};

const FeatureGroupSortableItem = ({ permission, canManage }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: normalizeId(permission.id),
        data: { type: 'API', permission },
        disabled: !canManage,
    });

    return (
        <div
            ref={setNodeRef}
            style={{
                transform: CSS.Transform.toString(transform),
                transition,
                opacity: isDragging ? 0.4 : 1,
                cursor: canManage ? 'grab' : 'default',
            }}
            className="am-api-row"
            {...attributes}
            {...(canManage ? listeners : {})}
        >
            <div className="am-api-title">{getPermissionName(permission)}</div>
            <div className="am-api-controls">
                <ActionPill action={permission.action} />
            </div>
        </div>
    );
};

const FeaturesTab = ({ permissions, featureGroups, canManage, onFeatureGroupsChange, onPermissionsChange }) => {
    const [search, setSearch] = useState('');
    const [newGroupName, setNewGroupName] = useState('');
    const [newGroupDesc, setNewGroupDesc] = useState('');
    const [editingGroupId, setEditingGroupId] = useState(null);
    const [editGroupName, setEditGroupName] = useState('');
    const [editGroupDesc, setEditGroupDesc] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [activeDragItem, setActiveDragItem] = useState(null);
    const [localPermissions, setLocalPermissions] = useState(permissions);

    useEffect(() => { setLocalPermissions(permissions); }, [permissions]);

    const filteredPermissions = useMemo(
        () => localPermissions.filter((p) => permissionMatchesSearch(p, search)),
        [localPermissions, search]
    );

    const groupedFeatures = useMemo(
        () => groupPermissionsByFeature(filteredPermissions, featureGroups),
        [filteredPermissions, featureGroups]
    );

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const refreshFeatureGroups = useCallback(async () => {
        const res = await api.get('/access/features', { params: { _t: Date.now() } });
        const next = Array.isArray(res.data) ? res.data : [];
        onFeatureGroupsChange(next);
        return next;
    }, [onFeatureGroupsChange]);

    const handleCreateFeatureGroup = async () => {
        if (!newGroupName.trim() || !canManage) return;
        setSaving(true); setError(null);
        try {
            await api.post('/access/features', { name: newGroupName.trim(), description: newGroupDesc.trim() });
            setNewGroupName(''); setNewGroupDesc('');
            await refreshFeatureGroups();
        } catch (err) { setError(await extractErrorMessage(err, 'Failed to create feature group.')); }
        finally { setSaving(false); }
    };

    const beginEditFeatureGroup = (group) => {
        setEditingGroupId(group.id);
        setEditGroupName(group.name || '');
        setEditGroupDesc(group.description || '');
    };

    const handleUpdateFeatureGroup = async () => {
        if (!canManage || !editingGroupId || !editGroupName.trim()) return;
        setSaving(true); setError(null);
        try {
            await api.put(`/access/features/${editingGroupId}`, {
                name: editGroupName.trim(),
                description: editGroupDesc.trim(),
            });
            setEditingGroupId(null); setEditGroupName(''); setEditGroupDesc('');
            await refreshFeatureGroups();
        } catch (err) { setError(await extractErrorMessage(err, 'Failed to update feature group.')); }
        finally { setSaving(false); }
    };

    const handleDeleteFeatureGroup = async (groupId, name) => {
        if (!canManage) return;
        if (!window.confirm(`Delete feature group "${name}"? APIs in it will become unassigned.`)) return;
        setSaving(true); setError(null);
        const nid = normalizeId(groupId);
        const nextPerms = localPermissions.map((p) =>
            normalizeId(p.featureGroupId) === nid ? { ...p, featureGroupId: null } : p
        );
        try {
            await api.delete(`/access/features/${groupId}`);
            setLocalPermissions(nextPerms);
            onPermissionsChange(nextPerms);
            await refreshFeatureGroups();
        } catch (err) { setError(await extractErrorMessage(err, 'Failed to delete feature group.')); }
        finally { setSaving(false); }
    };

    const assignPermissionToGroup = async (permissionId, targetFeatureGroupId) => {
        const nid = normalizeId(permissionId);
        const current = localPermissions.find((p) => normalizeId(p.id) === nid);
        if (!current) return;
        const targetId = normalizeId(targetFeatureGroupId);
        if (normalizeId(current.featureGroupId) === targetId) return;
        const prev = localPermissions;
        const next = localPermissions.map((p) =>
            normalizeId(p.id) === nid ? { ...p, featureGroupId: targetFeatureGroupId || null } : p
        );
        setLocalPermissions(next);
        onPermissionsChange(next);
        try {
            await api.put(`/access/permissions/${current.id}`, {
                name: current.name,
                action: current.action,
                featureGroupId: targetFeatureGroupId || null,
            });
        } catch (err) {
            setLocalPermissions(prev);
            onPermissionsChange(prev);
            setError(await extractErrorMessage(err, 'Failed to move API to target feature group.'));
        }
    };

    const handleDragStart = (event) => {
        setActiveDragItem(event?.active?.data?.current?.permission || null);
    };

    const handleDragEnd = async (event) => {
        setActiveDragItem(null);
        if (!canManage) return;
        const { active, over } = event;
        if (!over) return;
        const permissionId = normalizeId(active.id);
        if (!permissionId) return;
        let targetGroupId = null;
        if (over?.data?.current?.type === 'GROUP') targetGroupId = over.data.current.groupId;
        else if (over?.data?.current?.type === 'API') targetGroupId = over.data.current.permission?.featureGroupId || UNASSIGNED_KEY;
        else if (String(over.id || '').startsWith('group:')) targetGroupId = String(over.id).slice('group:'.length);
        if (!targetGroupId) return;
        await assignPermissionToGroup(permissionId, targetGroupId === UNASSIGNED_KEY ? null : targetGroupId);
    };

    return (
        <div className="am-catalog-screen">
            <div className="am-section-toolbar">
                <div className="am-section-title-block">
                    <h2>Feature Groups</h2>
                    <p>Organise your API permissions into named groups. Drag APIs between groups to reclassify them.</p>
                </div>
                <div className="am-search-wrap">
                    <FaSearch className="am-search-icon" size={13} />
                    <input
                        className="am-search-input"
                        placeholder="Search APIs or groups…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>

            {error && <InlineAlert type="error" onDismiss={() => setError(null)}>{error}</InlineAlert>}

            {canManage && !editingGroupId && (
                <div className="am-create-group-row">
                    <input
                        className="am-input"
                        placeholder="New group name…"
                        value={newGroupName}
                        onChange={(e) => setNewGroupName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleCreateFeatureGroup()}
                    />
                    <input
                        className="am-input"
                        placeholder="Description (optional)"
                        value={newGroupDesc}
                        onChange={(e) => setNewGroupDesc(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleCreateFeatureGroup()}
                    />
                    <button
                        type="button"
                        className="am-primary-btn"
                        disabled={!newGroupName.trim() || saving}
                        onClick={handleCreateFeatureGroup}
                    >
                        {saving ? <Spinner animation="border" size="sm" /> : <><FaPlus size={10} /> Create Group</>}
                    </button>
                </div>
            )}

            {canManage && editingGroupId && (
                <div className="am-edit-group-row">
                    <input
                        className="am-input"
                        placeholder="Group name…"
                        value={editGroupName}
                        onChange={(e) => setEditGroupName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleUpdateFeatureGroup()}
                    />
                    <input
                        className="am-input"
                        placeholder="Description (optional)"
                        value={editGroupDesc}
                        onChange={(e) => setEditGroupDesc(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleUpdateFeatureGroup()}
                    />
                    <button
                        type="button"
                        className="am-primary-btn"
                        disabled={!editGroupName.trim() || saving}
                        onClick={handleUpdateFeatureGroup}
                    >
                        Save Changes
                    </button>
                    <button
                        type="button"
                        className="am-icon-btn"
                        onClick={() => { setEditingGroupId(null); setEditGroupName(''); setEditGroupDesc(''); }}
                        aria-label="Cancel edit"
                    >
                        <FaTimes size={12} />
                    </button>
                </div>
            )}

            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
            >
                <div className="am-feature-catalog-list">
                    {groupedFeatures.length === 0 && (
                        <div className="am-empty-state compact">
                            <p>No APIs matched your search.</p>
                        </div>
                    )}

                    {groupedFeatures.map((fg) => {
                        const Icon = fg.feature.icon || FaLayerGroup;
                        const isUnassigned = fg.featureKey === UNASSIGNED_KEY;
                        const sortableIds = fg.permissions.map((p) => normalizeId(p.id));

                        return (
                            <SortableContext key={fg.featureKey} items={sortableIds} strategy={verticalListSortingStrategy}>
                                <article className="am-feature-section">
                                    <header className="am-feature-section-header">
                                        <div className="am-feature-section-title">
                                            <span className="am-feature-section-icon"><Icon /></span>
                                            <div>
                                                <h4>{fg.feature.label}</h4>
                                                {fg.feature.description && <p>{fg.feature.description}</p>}
                                            </div>
                                        </div>

                                        <div className="am-feature-section-controls">
                                            <span className="am-progress-badge">{fg.permissions.length} APIs</span>
                                            {canManage && !isUnassigned && (
                                                <>
                                                    <button
                                                        type="button"
                                                        className="am-icon-btn"
                                                        onClick={() => beginEditFeatureGroup({
                                                            id: fg.feature.id,
                                                            name: fg.feature.name,
                                                            description: fg.feature.description,
                                                        })}
                                                        aria-label={`Edit ${fg.feature.label}`}
                                                    >
                                                        <FaCog size={12} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="am-icon-btn danger"
                                                        onClick={() => handleDeleteFeatureGroup(fg.feature.id, fg.feature.label)}
                                                        aria-label={`Delete ${fg.feature.label}`}
                                                    >
                                                        <FaTrash size={12} />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </header>

                                    <div className="am-feature-section-body">
                                        <FeatureGroupDropZone groupId={fg.featureKey}>
                                            {fg.permissions.length === 0 ? (
                                                <div className="am-api-row" style={{ justifyContent: 'center', color: '#8798bf' }}>
                                                    Drop APIs here
                                                </div>
                                            ) : (
                                                fg.permissions.map((permission) => (
                                                    <FeatureGroupSortableItem
                                                        key={permission.id}
                                                        permission={permission}
                                                        canManage={canManage}
                                                    />
                                                ))
                                            )}
                                        </FeatureGroupDropZone>
                                    </div>
                                </article>
                            </SortableContext>
                        );
                    })}
                </div>

                <DragOverlay>
                    {activeDragItem ? (
                        <div className="am-api-row" style={{ background: '#1f283f', borderColor: 'rgba(124, 151, 204, 0.5)' }}>
                            <div className="am-api-title">{getPermissionName(activeDragItem)}</div>
                            <div className="am-api-controls">
                                <ActionPill action={activeDragItem.action} />
                            </div>
                        </div>
                    ) : null}
                </DragOverlay>
            </DndContext>
        </div>
    );
};

// ── Register API Tab ──────────────────────────────────────────

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', '*'];
const PERMISSION_NAME_REGEX = /^(super_admin\.all_access|(core|reports|smart_tools)\.[a-z0-9_]+\.[a-z0-9_]+\.(read|create|update|delete|execute|manage))$/;
const PATH_TO_SERVICE = (apiPath) => {
    if (!apiPath) return '';
    if (apiPath.startsWith('/smart-tools/')) return 'smart_tools';
    if (apiPath.startsWith('/reports/')) return 'reports';
    if (apiPath.startsWith('/api/')) return 'core';
    return '';
};
const METHOD_TO_ACTION = (method, isWildcardName) => {
    if (isWildcardName) return 'manage';
    switch ((method || '').toUpperCase()) {
        case 'GET': return 'read';
        case 'POST': return 'create';
        case 'PUT':
        case 'PATCH': return 'update';
        case 'DELETE': return 'delete';
        case '*': return 'manage';
        default: return 'execute';
    }
};
const UI_AREA_OPTIONS = [
    { value: 'none', label: 'No UI entry (permission only)' },
    { value: 'sidebar', label: 'Sidebar' },
    { value: 'dashboard', label: 'Dashboard widget' },
    { value: 'both', label: 'Sidebar + dashboard' },
];
const ICON_OPTIONS = [
    'tools', 'tags', 'chart-bar', 'box-open', 'file-invoice-dollar', 'desktop',
    'search', 'users', 'cog', 'shield', 'folder-open', 'key', 'book', 'home', 'status',
];

const collectSidebarFlat = (items, depth = 0, out = []) => {
    if (!Array.isArray(items)) return out;
    items.forEach((item) => {
        if (!item?.id) return;
        out.push({ id: item.id, label: item.label || item.id, depth });
        if (Array.isArray(item.children) && item.children.length > 0) {
            collectSidebarFlat(item.children, depth + 1, out);
        }
    });
    return out;
};

const RegisterApiTab = ({ roles, featureGroups, canManage, onRegistered }) => {
    const [permName, setPermName] = useState('');
    const [permAction, setPermAction] = useState('execute');
    const [apiPath, setApiPath] = useState('');
    const [method, setMethod] = useState('POST');
    const [featureGroupId, setFeatureGroupId] = useState('');
    const [selectedRoleIds, setSelectedRoleIds] = useState(new Set());

    const [uiArea, setUiArea] = useState('sidebar');
    const [sidebarItems, setSidebarItems] = useState([]);
    const [parentId, setParentId] = useState('');
    const [uiId, setUiId] = useState('');
    const [uiLabel, setUiLabel] = useState('');
    const [uiDescription, setUiDescription] = useState('');
    const [uiIcon, setUiIcon] = useState('tools');
    const [uiRoute, setUiRoute] = useState('');
    const [uiAccent, setUiAccent] = useState('#3b82f6');
    const [uiSortOrder, setUiSortOrder] = useState(50);
    const [uiAllowedRoles, setUiAllowedRoles] = useState(new Set());

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await api.get('/ui-config');
                if (cancelled) return;
                setSidebarItems(collectSidebarFlat(res?.data?.sidebarItems || []));
            } catch (_err) {
                if (!cancelled) setSidebarItems([]);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const selectedFeatureGroup = useMemo(
        () => featureGroups.find((fg) => normalizeId(fg.id) === normalizeId(featureGroupId)) || null,
        [featureGroups, featureGroupId]
    );

    const toggleRole = (id) => {
        setSelectedRoleIds((prev) => {
            const next = new Set(prev);
            const k = normalizeId(id);
            if (next.has(k)) next.delete(k); else next.add(k);
            return next;
        });
    };

    const toggleUiAllowedRole = (roleName) => {
        setUiAllowedRoles((prev) => {
            const next = new Set(prev);
            if (next.has(roleName)) next.delete(roleName); else next.add(roleName);
            return next;
        });
    };

    const reset = () => {
        setPermName(''); setApiPath(''); setUiId(''); setUiLabel('');
        setUiDescription(''); setUiRoute('');
        setSelectedRoleIds(new Set()); setUiAllowedRoles(new Set());
        setResult(null); setError(null);
    };

    const handleSubmit = async () => {
        setError(null); setResult(null);
        if (!canManage) { setError('Super Admin role is required.'); return; }
        if (!permName.trim() || !permAction.trim() || !apiPath.trim() || !method.trim()) {
            setError('Permission name, action, api_path, and method are required.'); return;
        }
        const includeUi = uiArea !== 'none';
        if (includeUi) {
            if (!uiId.trim() || !uiLabel.trim()) {
                setError('UI id and label are required when adding a UI entry.'); return;
            }
        }
        const featureGroupKey = selectedFeatureGroup?.key || '';
        const body = {
            permission: {
                name: permName.trim(),
                action: permAction.trim(),
                apiPath: apiPath.trim(),
                method: method.trim().toUpperCase(),
                featureGroupId: featureGroupId || null,
            },
            roleIds: Array.from(selectedRoleIds),
            uiItem: includeUi ? {
                area: uiArea,
                parentId: parentId || null,
                id: uiId.trim(),
                label: uiLabel.trim(),
                description: uiDescription.trim() || null,
                icon: uiIcon || null,
                route: uiRoute.trim() || null,
                featureGroupKey: featureGroupKey || null,
                allowedRoles: Array.from(uiAllowedRoles),
                accentColor: uiAccent || null,
                sortOrder: Number.isFinite(Number(uiSortOrder)) ? Number(uiSortOrder) : 0,
                type: 'link',
            } : null,
        };

        setSubmitting(true);
        try {
            const res = await api.post('/access/register-api', body);
            setResult(res.data);
            onRegistered?.();
        } catch (err) {
            setError(await extractErrorMessage(err, 'Failed to register API.'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="am-catalog-screen">
            <div className="am-section-toolbar">
                <div className="am-section-title-block">
                    <h2>Register a new API</h2>
                    <p>
                        One submit creates the MySQL permission row, grants it to the chosen roles, bumps
                        every affected employee's <code>authz_version</code> so Redis invalidates, and
                        (optionally) appends the matching UI entry to the portal config.
                    </p>
                </div>
            </div>

            {!canManage && (
                <InlineAlert type="info">Only Super Admins can register APIs.</InlineAlert>
            )}
            {error && <InlineAlert type="error" onDismiss={() => setError(null)}>{error}</InlineAlert>}
            {result && (
                <InlineAlert type="info" onDismiss={() => setResult(null)}>
                    Created <strong>{result.permission?.name}</strong>{' '}
                    · roles granted: {result.rolesUpdated?.length ?? 0}{' '}
                    · employees bumped: {result.employeesBumped ?? 0}
                    {result.uiPlacement ? ` · UI: ${result.uiPlacement} (v${result.uiConfigVersion})` : ' · no UI change'}
                </InlineAlert>
            )}

            <div className="am-register-grid">
                <section className="am-overview-card am-panel">
                    <header className="am-panel-head">
                        <p className="am-overview-card-title">1. Permission</p>
                        <p className="am-overview-card-desc">Row inserted into <code>permissions</code>.</p>
                    </header>

                    <div className="am-form-stack am-panel-body">
                        <label className="am-form-field">
                            <span>Name <em>(unique, e.g. smart_tools.variants_fetch.execute)</em></span>
                            <input
                                className="am-input"
                                value={permName}
                                onChange={(e) => setPermName(e.target.value)}
                                disabled={!canManage}
                            />
                        </label>
                        <label className="am-form-field">
                            <span>Action</span>
                            <input
                                className="am-input"
                                value={permAction}
                                onChange={(e) => setPermAction(e.target.value)}
                                disabled={!canManage}
                            />
                        </label>
                        <div className="am-form-row">
                            <label className="am-form-field am-form-field-grow">
                                <span>API Path (gateway prefix included)</span>
                                <input
                                    className="am-input"
                                    placeholder="/smart-tools/variant/fetch-variants"
                                    value={apiPath}
                                    onChange={(e) => setApiPath(e.target.value)}
                                    disabled={!canManage}
                                />
                            </label>
                            <label className="am-form-field">
                                <span>Method</span>
                                <select
                                    className="am-input"
                                    value={method}
                                    onChange={(e) => setMethod(e.target.value)}
                                    disabled={!canManage}
                                >
                                    {HTTP_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                                </select>
                            </label>
                        </div>
                        <label className="am-form-field">
                            <span>Feature Group</span>
                            <select
                                className="am-input"
                                value={featureGroupId}
                                onChange={(e) => setFeatureGroupId(e.target.value)}
                                disabled={!canManage}
                            >
                                <option value="">— Unassigned —</option>
                                {featureGroups.map((fg) => (
                                    <option key={fg.id} value={fg.id}>
                                        {fg.name}{fg.key ? ` (${fg.key})` : ''}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>
                </section>

                <section className="am-overview-card am-panel">
                    <header className="am-panel-head">
                        <p className="am-overview-card-title">2. Grant to roles</p>
                        <p className="am-overview-card-desc">
                            Inserts into <code>role_permissions</code> and bumps <code>authz_version</code>
                            for every employee in the selected role(s).
                        </p>
                    </header>
                    <div className="am-form-stack am-panel-body">
                        {roles.length === 0 && <p className="am-overview-card-desc">No roles loaded.</p>}
                        {roles.map((r) => (
                            <label key={r.id} className="am-role-toggle">
                                <input
                                    type="checkbox"
                                    checked={selectedRoleIds.has(normalizeId(r.id))}
                                    onChange={() => toggleRole(r.id)}
                                    disabled={!canManage}
                                />
                                <span>{r.name}</span>
                            </label>
                        ))}
                    </div>
                </section>

                <section className="am-overview-card am-register-ui-card am-panel">
                    <header className="am-panel-head">
                        <p className="am-overview-card-title">3. UI placement (optional)</p>
                        <p className="am-overview-card-desc">
                            Adds the entry to MongoDB <code>ui_configurations</code>. Visibility = featureGroupKey
                            + allowedRoles.
                        </p>
                    </header>

                    <div className="am-form-stack am-panel-body">
                        <label className="am-form-field">
                            <span>Where</span>
                            <select
                                className="am-input"
                                value={uiArea}
                                onChange={(e) => setUiArea(e.target.value)}
                                disabled={!canManage}
                            >
                                {UI_AREA_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </label>

                        {uiArea !== 'none' && (
                            <>
                                {(uiArea === 'sidebar' || uiArea === 'both') && (
                                    <label className="am-form-field">
                                        <span>Sidebar parent</span>
                                        <select
                                            className="am-input"
                                            value={parentId}
                                            onChange={(e) => setParentId(e.target.value)}
                                            disabled={!canManage}
                                        >
                                            <option value="">— Top level —</option>
                                            {sidebarItems.map((it) => (
                                                <option key={it.id} value={it.id}>
                                                    {`${'  '.repeat(it.depth)}${it.label}`}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                )}

                                <div className="am-form-row">
                                    <label className="am-form-field am-form-field-grow">
                                        <span>UI id (kebab-case, unique)</span>
                                        <input
                                            className="am-input"
                                            placeholder="my-new-tool"
                                            value={uiId}
                                            onChange={(e) => setUiId(e.target.value)}
                                            disabled={!canManage}
                                        />
                                    </label>
                                    <label className="am-form-field">
                                        <span>Sort order</span>
                                        <input
                                            className="am-input"
                                            type="number"
                                            value={uiSortOrder}
                                            onChange={(e) => setUiSortOrder(e.target.value)}
                                            disabled={!canManage}
                                        />
                                    </label>
                                </div>

                                <label className="am-form-field">
                                    <span>Label</span>
                                    <input
                                        className="am-input"
                                        value={uiLabel}
                                        onChange={(e) => setUiLabel(e.target.value)}
                                        disabled={!canManage}
                                    />
                                </label>
                                <label className="am-form-field">
                                    <span>Description</span>
                                    <input
                                        className="am-input"
                                        value={uiDescription}
                                        onChange={(e) => setUiDescription(e.target.value)}
                                        disabled={!canManage}
                                    />
                                </label>
                                <div className="am-form-row">
                                    <label className="am-form-field">
                                        <span>Icon</span>
                                        <select
                                            className="am-input"
                                            value={uiIcon}
                                            onChange={(e) => setUiIcon(e.target.value)}
                                            disabled={!canManage}
                                        >
                                            {ICON_OPTIONS.map((ic) => (
                                                <option key={ic} value={ic}>{ic}</option>
                                            ))}
                                        </select>
                                    </label>
                                    <label className="am-form-field am-form-field-grow">
                                        <span>Frontend route</span>
                                        <input
                                            className="am-input"
                                            placeholder="/tools/my-new-tool"
                                            value={uiRoute}
                                            onChange={(e) => setUiRoute(e.target.value)}
                                            disabled={!canManage}
                                        />
                                    </label>
                                </div>
                                <label className="am-form-field">
                                    <span>Accent color</span>
                                    <input
                                        className="am-input"
                                        value={uiAccent}
                                        onChange={(e) => setUiAccent(e.target.value)}
                                        disabled={!canManage}
                                    />
                                </label>
                                <div className="am-form-field">
                                    <span>Allowed roles (UI visibility)</span>
                                    <div className="am-chip-toggle-row">
                                        {roles.map((r) => (
                                            <label key={r.id} className="am-role-toggle">
                                                <input
                                                    type="checkbox"
                                                    checked={uiAllowedRoles.has(r.name)}
                                                    onChange={() => toggleUiAllowedRole(r.name)}
                                                    disabled={!canManage}
                                                />
                                                <span>{r.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </section>
            </div>

            <div className="am-register-actions">
                <button
                    type="button"
                    className="am-primary-btn"
                    onClick={handleSubmit}
                    disabled={submitting || !canManage}
                >
                    {submitting ? <Spinner animation="border" size="sm" /> : <><FaPlus size={10} /> Register API</>}
                </button>
                <button type="button" className="am-icon-btn" onClick={reset} disabled={submitting}>
                    Reset form
                </button>
            </div>
        </div>
    );
};

// ── Root Component ────────────────────────────────────────────

const AccessManagement = () => {
    const { user } = useContext(AuthContext);
    const canManage = isSuperAdminUser(user);

    const [activeTab, setActiveTab] = useState('overview');
    const [roles, setRoles] = useState([]);
    const [permissions, setPermissions] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [featureGroups, setFeatureGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const groupedFeatures = useMemo(
        () => groupPermissionsByFeature(permissions, featureGroups),
        [permissions, featureGroups]
    );

    const fetchData = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const [rolesRes, permsRes, empsRes, featRes] = await Promise.all([
                api.get('/access/roles', { params: { _t: Date.now() } }),
                api.get('/access/permissions', { params: { _t: Date.now() } }),
                api.get('/employees', { params: { _t: Date.now() } }),
                api.get('/access/features', { params: { _t: Date.now() } }),
            ]);
            setRoles(Array.isArray(rolesRes.data) ? rolesRes.data : []);
            setPermissions(Array.isArray(permsRes.data) ? permsRes.data : []);
            setEmployees(Array.isArray(empsRes.data) ? empsRes.data : []);
            setFeatureGroups(Array.isArray(featRes.data) ? featRes.data : []);
        } catch (err) {
            setError(await extractErrorMessage(err, 'Failed to load access management data.'));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    if (loading) {
        return (
            <div className="am-page">
                <div className="am-loading">
                    <Spinner animation="border" className="am-spinner" />
                    <p className="am-loading-text">Loading access management…</p>
                </div>
            </div>
        );
    }

    return (
        <div className="am-page">
            {/* Page header */}
            <header className="am-page-header">
                <div className="am-page-header-inner">
                    <span className="am-header-icon"><FaShieldAlt /></span>
                    <div>
                        <h1>Access Management</h1>
                        <p>Control who can do what — manage roles, permissions, and employee access.</p>
                    </div>
                </div>

                <div className="am-header-stats">
                    <div className="am-stat-pill">
                        <span>Roles</span>
                        <strong>{roles.length}</strong>
                    </div>
                    <div className="am-stat-pill">
                        <span>Employees</span>
                        <strong>{employees.length}</strong>
                    </div>
                    <div className="am-stat-pill">
                        <span>API Permissions</span>
                        <strong>{permissions.length}</strong>
                    </div>
                    <div className="am-stat-pill">
                        <span>Feature Groups</span>
                        <strong>{featureGroups.length}</strong>
                    </div>
                </div>
            </header>

            {/* Tab bar */}
            <nav className="am-tabs-bar">
                {TABS.map((tab) => {
                    const Icon = tab.icon;
                    const active = activeTab === tab.id;
                    return (
                        <button
                            key={tab.id}
                            type="button"
                            className={`am-tab-btn ${active ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            <span className="am-tab-icon"><Icon /></span>
                            <span className="am-tab-label">
                                <strong>{tab.label}</strong>
                                <small>{tab.subtitle}</small>
                            </span>
                        </button>
                    );
                })}
            </nav>

            {/* Global alerts */}
            {(error || !canManage) && (
                <div className="am-alerts-bar">
                    {error && (
                        <InlineAlert type="error" onDismiss={() => setError(null)}>{error}</InlineAlert>
                    )}
                    {!canManage && (
                        <InlineAlert type="info">
                            You're in read-only mode. Only Super Admins can make changes.
                        </InlineAlert>
                    )}
                </div>
            )}

            {/* Tab content */}
            <div className="am-screen-body">
                {activeTab === 'overview' && (
                    <OverviewTab
                        roles={roles}
                        employees={employees}
                        permissions={permissions}
                        groupedFeatures={groupedFeatures}
                        onNavigate={setActiveTab}
                    />
                )}
                {activeTab === 'roles' && (
                    <RolesTab
                        roles={roles}
                        permissions={permissions}
                        featureGroups={featureGroups}
                        canManage={canManage}
                        onRolesChange={setRoles}
                    />
                )}
                {activeTab === 'employees' && (
                    <EmployeesTab
                        roles={roles}
                        employees={employees}
                        canManage={canManage}
                        onEmployeesChange={setEmployees}
                    />
                )}
                {activeTab === 'features' && (
                    <FeaturesTab
                        permissions={permissions}
                        featureGroups={featureGroups}
                        canManage={canManage}
                        onFeatureGroupsChange={setFeatureGroups}
                        onPermissionsChange={setPermissions}
                    />
                )}
                {activeTab === 'register-api' && (
                    <RegisterApiTab
                        roles={roles}
                        featureGroups={featureGroups}
                        canManage={canManage}
                        onRegistered={fetchData}
                    />
                )}
            </div>
        </div>
    );
};

export default AccessManagement;
